import { useSendGoogleDriveFilteToTelegram } from '@/shared/api'
import {
    CheckmarkBlueSVGIcon,
    DocumentIcon,
    EditPencilIcon,
    GoogleDriveGraySVGIcon,
    ShareSVGIcon,
    TrashIcon,
} from '@/shared/assets'
import {
    Command,
    CommandGroup,
    CommandItem,
    CommandList,
    Input,
    ShadcnButton,
} from '@/shared/ui'
import { Popover, PopoverContent, PopoverTrigger } from '@/shared/ui/popover'
import { cn } from '@/true-fsd/shared/utils/css'
import { DocumentRecord } from '@synapex/prisma/client'
import { CustomActWithType } from '@synapex/shared2/gateway-rest-api'
import {
    Check,
    ChevronDown,
    EllipsisVerticalIcon,
    Loader2,
    PlusIcon,
    XIcon,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export function DraftActFileField({
    act,
    customAct,
    onClickCreate,
    onClickEditCustomAct,
    onDeleteItem,
    onDeleteCustomAct,
    onUpdateDefaultActNumber,
    disabled,
    error,
    isLoading,
    className,
}: {
    act: DocumentRecord | undefined
    customAct?: CustomActWithType
    onClickCreate: (type: 'default' | 'custom') => void
    onClickEditCustomAct?: () => void
    onDeleteItem: (
        type: 'draft' | 'final',
        document: DocumentRecord
    ) => Promise<void>
    onDeleteCustomAct?: () => Promise<void>
    onUpdateDefaultActNumber?: (
        documentRecordId: number,
        actNumber: string
    ) => Promise<void>
    disabled?: boolean
    error?: boolean
    isLoading?: boolean
    className?: string
}) {
    const [open, setOpen] = useState(false)
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
    const [editingDefaultActId, setEditingDefaultActId] = useState<
        number | null
    >(null)
    const [editingDefaultActValue, setEditingDefaultActValue] = useState('')

    const sendToTelegram = useSendGoogleDriveFilteToTelegram()
    const editingInputRef = useRef<HTMLInputElement | null>(null)

    useEffect(() => {
        if (isLoading) {
            setOpen(false)
        }
    }, [isLoading])

    useEffect(() => {
        if (editingDefaultActId !== null) {
            editingInputRef.current?.focus()
            editingInputRef.current?.select()
        }
    }, [editingDefaultActId])

    useEffect(() => {
        if (editingDefaultActId !== null && act) {
            setEditingDefaultActValue(
                act.custom_number ?? act.compositeNumber?.toString() ?? ''
            )
        }
    }, [editingDefaultActId, act])

    function handleCancelDelete(event: React.MouseEvent) {
        event.stopPropagation()
        setShowDeleteConfirmation(false)
    }

    function handleDeleteConfirmation() {
        if (!showDeleteConfirmation) {
            setShowDeleteConfirmation(true)
        }
    }

    async function handleDelete() {
        if (act) {
            await onDeleteItem('draft', act)
        } else if (customAct) {
            await onDeleteCustomAct?.()
        }
        setShowDeleteConfirmation(false)
        setOpen(false)
    }

    function handleOpenGoogleDriveFolder(document: DocumentRecord) {
        if (document.unsignedLink) {
            window.open(document.unsignedLink, '_blank')
        }
    }

    function handleSendToSelf(document: DocumentRecord) {
        setOpen(false)
        sendToTelegram.mutate(document.documentRecordId)
    }

    function handleCreate(type: 'default' | 'custom') {
        onClickCreate(type)
        setOpen(false)
    }

    function handleEditDefaultAct() {
        if (act?.documentRecordId) {
            setEditingDefaultActId(act.documentRecordId)
            setEditingDefaultActValue(
                act.custom_number ?? act.compositeNumber?.toString() ?? ''
            )
            setOpen(false)
        }
    }

    function handleCancelEditDefaultAct() {
        setEditingDefaultActId(null)
        setEditingDefaultActValue('')
    }

    async function handleConfirmRenameDefaultAct() {
        if (!act || !act.documentRecordId) {
            return
        }

        try {
            await onUpdateDefaultActNumber?.(
                act.documentRecordId,
                editingDefaultActValue
            )
            handleCancelEditDefaultAct()
        } catch (error) {
            console.error(error)
        }
    }

    const finalLoadingStatus = isLoading || sendToTelegram.isPending
    const hasSelectedAct = !!act
    const hasSelectedCustomAct = !!customAct
    const hasSelection = hasSelectedAct || hasSelectedCustomAct
    const isEditingSelectedDefaultAct =
        editingDefaultActId !== null &&
        act?.documentRecordId === editingDefaultActId

    const renderTriggerLabel = () => {
        if (customAct) {
            return (
                <div className="flex max-w-full items-center gap-1">
                    <img src={DocumentIcon} className="h-4 w-3" />
                    <span className="flex-1 truncate">{customAct.number}</span>
                </div>
            )
        }

        if (act) {
            return (
                <div className="flex max-w-full items-center gap-1">
                    <img src={DocumentIcon} className="h-4 w-3" />
                    <span className="flex-1 truncate">{act.fileName}</span>
                </div>
            )
        }

        return (
            <>
                <span className="overflow-hidden text-ellipsis">
                    Сгенерировать акт
                </span>
                {!finalLoadingStatus && <ChevronDown className="h-3 w-3" />}
            </>
        )
    }

    const renderEditDefaultTrigger = () => {
        if (!isEditingSelectedDefaultAct || !act) {
            return null
        }

        return (
            <div
                className={cn(
                    'flex w-48 items-center justify-between p-0 text-xs font-normal',
                    className,
                    error && !hasSelection && 'border-red-500'
                )}
                onClick={(event) => event.stopPropagation()}
            >
                <Input
                    shouldShowClearButton={false}
                    innerClassName="h-[32px] pr-2"
                    disabled={isLoading}
                    ref={editingInputRef}
                    value={editingDefaultActValue}
                    onChange={(value) => setEditingDefaultActValue(value)}
                    onClick={(event) => event.stopPropagation()}
                    onKeyDown={(event) => {
                        event.stopPropagation()
                        if (event.key === 'Enter') {
                            event.preventDefault()
                            void handleConfirmRenameDefaultAct()
                        }

                        if (event.key === 'Escape') {
                            event.preventDefault()
                            handleCancelEditDefaultAct()
                        }
                    }}
                    placeholder="Введите номер акта"
                />
                <div
                    className="absolute right-2 top-1/2 z-[1000] -translate-y-1/2 cursor-pointer"
                    onClick={handleConfirmRenameDefaultAct}
                >
                    {isLoading ? (
                        <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
                    ) : (
                        <CheckmarkBlueSVGIcon className="h-4 w-4 shrink-0 text-blue-primary" />
                    )}
                </div>
            </div>
        )
    }

    const renderTrigger = () => {
        const editTrigger = renderEditDefaultTrigger()
        if (editTrigger) {
            return editTrigger
        }

        return (
            <ShadcnButton
                variant="outline"
                role="combobox"
                aria-expanded={open}
                className={cn(
                    'flex h-auto items-center justify-between px-[10px] py-2 text-xs font-normal',
                    className,
                    error && !hasSelection && 'border-red-500'
                )}
            >
                {renderTriggerLabel()}
            </ShadcnButton>
        )
    }

    const renderRightControl = () => {
        if (isEditingSelectedDefaultAct) {
            return null
        }

        if (finalLoadingStatus) {
            return <Loader2 className="h-4 w-4 shrink-0 animate-spin" />
        }

        if (!hasSelection) {
            return null
        }

        return (
            <div
                onClick={(event) => {
                    event.stopPropagation()
                    setOpen(true)
                }}
            >
                <EllipsisVerticalIcon className="h-4 w-4 shrink-0" />
            </div>
        )
    }

    const renderDeleteAction = () => (
        <CommandItem
            className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
            onSelect={handleDeleteConfirmation}
        >
            <span>Удалить</span>
            {showDeleteConfirmation ? (
                <div className="flex justify-between">
                    <div onClick={handleDelete}>
                        <Check className="ml-2 cursor-pointer text-[#056FFF]" />
                    </div>
                    <div onClick={handleCancelDelete}>
                        <XIcon className="cursor-pointer" />
                    </div>
                </div>
            ) : (
                <img src={TrashIcon} />
            )}
        </CommandItem>
    )

    const renderMenuContent = () => {
        if (customAct) {
            const enabledOptions = (
                <>
                    {onClickEditCustomAct && (
                        <CommandItem
                            className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                            onSelect={() => {
                                onClickEditCustomAct()
                                setOpen(false)
                            }}
                        >
                            <span>Редактировать</span>
                            <EditPencilIcon className="h-4 w-4" />
                        </CommandItem>
                    )}
                    {renderDeleteAction()}
                </>
            )

            const disabledOptions = (
                <>
                    <CommandItem className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light">
                        <span className="w-full break-words">
                            {customAct.number}
                        </span>
                    </CommandItem>
                </>
            )

            const Items = disabled ? disabledOptions : enabledOptions

            return <CommandGroup className="p-0">{Items}</CommandGroup>
        }

        if (act) {
            return (
                <CommandGroup className="p-0">
                    <CommandItem
                        className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                        onSelect={() => handleSendToSelf(act)}
                    >
                        <span>Отправить себе в PDF</span>
                        <ShareSVGIcon className="stroke-blue-primary" />
                    </CommandItem>
                    {!disabled && (
                        <CommandItem
                            className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                            onSelect={handleEditDefaultAct}
                        >
                            <span>Редактировать</span>
                            <EditPencilIcon className="h-4 w-4" />
                        </CommandItem>
                    )}
                    <CommandItem
                        className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                        onSelect={() => handleOpenGoogleDriveFolder(act)}
                    >
                        <span>Ссылка на документ</span>
                        <GoogleDriveGraySVGIcon />
                    </CommandItem>
                    {!disabled && renderDeleteAction()}
                </CommandGroup>
            )
        }

        return (
            <CommandGroup className="p-0">
                <CommandItem
                    className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                    onSelect={() => handleCreate('default')}
                >
                    <span>Сгенерировать акт</span>
                    <PlusIcon className="h-3 w-3" />
                </CommandItem>
                <CommandItem
                    className="flex cursor-pointer justify-between rounded p-[6px] text-xs font-normal hover:bg-synapex-blue-light"
                    onSelect={() => handleCreate('custom')}
                >
                    <span>Ввести свой акт</span>
                    <PlusIcon className="h-3 w-3" />
                </CommandItem>
            </CommandGroup>
        )
    }

    return (
        <Popover open={open} onOpenChange={setOpen} modal>
            <div className="relative">
                <PopoverTrigger
                    asChild
                    disabled={disabled || finalLoadingStatus}
                >
                    {renderTrigger()}
                </PopoverTrigger>

                <div className="absolute right-3 top-2 cursor-pointer">
                    {renderRightControl()}
                </div>
            </div>
            <PopoverContent
                className={cn('w-56 p-0', className)}
                align="start"
                sideOffset={4}
                style={{ maxHeight: '200px', overflow: 'scroll' }}
            >
                <Command shouldFilter={false}>
                    <CommandList className="max-h-[300px] overflow-y-scroll rounded-lg p-2">
                        {renderMenuContent()}
                    </CommandList>
                </Command>
            </PopoverContent>
        </Popover>
    )
}
